import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Fade,
  CircularProgress,
} from '@mui/material';
import {
  AutoStories,
  Search,
  Explore,
  Favorite,
  Nightlight,
  Castle,
} from '@mui/icons-material';
import axios from 'axios';

const genres = [
  {
    name: 'Fantasy',
    icon: <Castle sx={{ fontSize: 60 }} />,
    description: 'Magical realms, mystical creatures, and enchanted adventures',
    color: '#9B59B6',
  },
  {
    name: 'Mystery',
    icon: <Search sx={{ fontSize: 60 }} />,
    description: 'Unravel secrets, solve puzzles, and discover hidden truths',
    color: '#34495E',
  },
  {
    name: 'Adventure',
    icon: <Explore sx={{ fontSize: 60 }} />,
    description: 'Thrilling quests, exotic lands, and daring escapades',
    color: '#E67E22',
  },
  {
    name: 'Romance',
    icon: <Favorite sx={{ fontSize: 60 }} />,
    description: 'Heartfelt encounters, true love, and enchanting moments',
    color: '#E91E63',
  },
  {
    name: 'Horror',
    icon: <Nightlight sx={{ fontSize: 60 }} />,
    description: 'Mysterious atmospheres, spine-tingling suspense, and dark secrets',
    color: '#2C3E50',
  },
  {
    name: 'Fairy Tale',
    icon: <AutoStories sx={{ fontSize: 60 }} />,
    description: 'Classic tales of magic, wonder, and happily ever after',
    color: '#FF6B9D',
  },
];

function StoryGenreSelector() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [selectedGenre, setSelectedGenre] = useState(null);

  const handleGenreSelect = async (genre) => {
    setSelectedGenre(genre);
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API_BASE_URL}/stories/create`,
        {
          genre: genre.name,
          userId: 'princess-nabilah', // TODO: Get from auth context
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      // Navigate to the new story
      navigate(`/story/${response.data._id}`);
    } catch (error) {
      console.error('Error creating story:', error);
      setLoading(false);
      setSelectedGenre(null);
      alert('Failed to create story. Please try again.');
    }
  };

  return (
    <Box sx={{ backgroundColor: '#FAF3E0', minHeight: '100vh', py: 6 }}>
      <Container maxWidth="lg">
        {/* Header */}
        <Fade in timeout={600}>
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <AutoStories sx={{ fontSize: 64, color: '#8D7B68', mb: 2 }} />
            <Typography
              variant="h3"
              sx={{
                fontWeight: 700,
                color: '#333',
                mb: 2,
                fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
              }}
            >
              Choose Your Tale, Your Majesty
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#555',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                maxWidth: '700px',
                mx: 'auto',
              }}
            >
              Select a genre to embark upon a magnificent adventure crafted especially for Your Royal Highness. Each choice shall shape your destiny!
            </Typography>
          </Box>
        </Fade>

        {/* Loading State */}
        {loading && (
          <Fade in>
            <Box sx={{ textAlign: 'center', my: 8 }}>
              <CircularProgress
                size={60}
                sx={{ color: '#8D7B68', mb: 3 }}
              />
              <Typography variant="h6" sx={{ color: '#8D7B68', fontWeight: 600 }}>
                The Royal Scribe prepares your tale...
              </Typography>
              <Typography variant="body2" sx={{ color: '#555', mt: 1 }}>
                Summoning visions from the realm of {selectedGenre?.name}
              </Typography>
            </Box>
          </Fade>
        )}

        {/* Genre Grid */}
        {!loading && (
          <Grid container spacing={3}>
            {genres.map((genre, index) => (
              <Grid item xs={12} sm={6} md={4} key={genre.name}>
                <Fade in timeout={800 + index * 100}>
                  <Card
                    sx={{
                      height: '100%',
                      backgroundColor: '#F4ECE3',
                      border: '2px solid #8D7B68',
                      borderRadius: 3,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: `0 8px 24px ${genre.color}40`,
                        borderColor: genre.color,
                      },
                    }}
                  >
                    <CardActionArea
                      onClick={() => handleGenreSelect(genre)}
                      sx={{ height: '100%', p: 3 }}
                    >
                      <CardContent sx={{ textAlign: 'center', p: 0 }}>
                        <Box sx={{ color: genre.color, mb: 2 }}>
                          {genre.icon}
                        </Box>
                        <Typography
                          variant="h5"
                          sx={{
                            fontWeight: 700,
                            color: '#333',
                            mb: 1.5,
                          }}
                        >
                          {genre.name}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: '#555',
                            lineHeight: 1.6,
                            fontSize: '0.95rem',
                          }}
                        >
                          {genre.description}
                        </Typography>
                      </CardContent>
                    </CardActionArea>
                  </Card>
                </Fade>
              </Grid>
            ))}
          </Grid>
        )}

        {/* Footer Note */}
        {!loading && (
          <Fade in timeout={1400}>
            <Box sx={{ textAlign: 'center', mt: 6 }}>
              <Typography variant="body2" sx={{ color: '#777', fontStyle: 'italic' }}>
                ✨ Each tale shall unfold uniquely based upon Your Majesty's choices ✨
              </Typography>
            </Box>
          </Fade>
        )}
      </Container>
    </Box>
  );
}

export default StoryGenreSelector;

